/*
  TASONI — GSAP scroll motion (subtle / editorial)
  --------------------------------------------------
  One centralized engine. For every content section it gently reveals the
  headings, text, cards and buttons as they scroll into view (fade + rise,
  soft stagger). No per-section JS needed.

  Reveal is SCROLL-DRIVEN (a throttled scroll pass), not ScrollTrigger's
  `once` callback — so it is bulletproof against fast/flick scrolling, anchor
  jumps and browser back-forward scroll restoration (which can skip
  ScrollTrigger.batch / once triggers and leave content stuck invisible).

  Safety:
  - Requires window.gsap. If it's missing, NOTHING is hidden — the page renders
    normally.
  - Respects prefers-reduced-motion (no motion, content stays visible).
  - Only elements still BELOW the fold on load are hidden, so already-visible
    content (the hero) never flickers.
  - Re-runs on shopify:section:load so it works in the theme editor.
  - Never touches the header, footer, fixed corner logo, carousels/marquees,
    or the collection product scatter.

  Opt-out on any element (or ancestor) with  data-no-anim.
*/
(function () {
  'use strict';

  var STARTED = false;

  var EXCLUDE = [
    '.header', '.header-wrapper', '.section-header', '.announcement-bar',
    'footer', '.footer', '.shopify-section-group-footer-group',
    '[data-no-anim]', '.tasoni-corner-logo', '.page-guide', '.tsf-bar',
    '.tasoni-scatter', '.mobile-facets', '.menu-drawer', '.cart-drawer'
  ];

  // Anything inside a carousel/marquee keeps its own motion.
  var CAROUSEL_SEL =
    '[class*="track"],[class*="__slide"],[class*="slideshow"],[class*="swiper"],' +
    '[class*="carousel"],[class*="marquee"]';

  // Content worth revealing — text, CTAs and block groups. Deliberately NOT big
  // media/panels, so we never fade a whole hero image in (reads as a slow load).
  var REVEAL_SEL = [
    'h1', 'h2', 'h3',
    '[class*="__title"]', '[class*="__heading"]', '[class*="__subtitle"]',
    '[class*="__description"]',
    '[class*="__card"]', '[class*="edition"]', '[class*="look_"]', '[class*="__look"]',
    '[class*="__row"]',
    '.button', '[class*="__btn"]', '[class*="__button"]', '[class*="__cta"]'
  ].join(',');

  var REVEAL_AT = 0.9;   // reveal once an element's top passes 90% of the viewport
  var reduced = false;

  // Robust viewport height — never 0 (a 0 would hide everything and never reveal).
  function vpH() {
    return window.innerHeight || document.documentElement.clientHeight || 0;
  }

  function closestMatch(el, list) {
    for (var i = 0; i < list.length; i++) { if (el.closest && el.closest(list[i])) return true; }
    return false;
  }

  // Keep the outermost block within a branch so we animate it once as a unit.
  function dedupeNesting(nodes) {
    return nodes.filter(function (el) {
      for (var i = 0; i < nodes.length; i++) {
        if (nodes[i] !== el && nodes[i].contains(el)) return false;
      }
      return true;
    });
  }

  function initSection(section) {
    if (!section || section.__tsMotion) return;
    if (closestMatch(section, EXCLUDE)) return;
    section.__tsMotion = true;

    var candidates = Array.prototype.slice.call(section.querySelectorAll(REVEAL_SEL))
      .filter(function (el) {
        if (closestMatch(el, EXCLUDE)) return false;
        if (el.closest(CAROUSEL_SEL)) return false;
        if (el.hasAttribute('data-anim-done')) return false;
        var r = el.getBoundingClientRect();
        return !(r.width === 0 && r.height === 0);
      });

    candidates = dedupeNesting(candidates);
    if (!candidates.length) return;

    // Only hide what's still below the fold; visible content stays put (no flicker).
    var vh = vpH();
    var toHide = candidates.filter(function (el) {
      return el.getBoundingClientRect().top > vh * REVEAL_AT;
    });
    if (!toHide.length) return;

    toHide.forEach(function (el) { el.setAttribute('data-anim-done', 'pending'); });
    gsap.set(toHide, { opacity: 0, y: 26 });
  }

  // Reveal everything that has reached the trigger line, grouped per pass so
  // items entering together get a soft stagger.
  function revealPass() {
    if (reduced) return;
    var vh = vpH();
    var batch = [];
    var pending = document.querySelectorAll('[data-anim-done="pending"]');
    for (var i = 0; i < pending.length; i++) {
      var el = pending[i];
      if (el.getBoundingClientRect().top < vh * REVEAL_AT) {
        el.setAttribute('data-anim-done', 'shown');
        batch.push(el);
      }
    }
    if (batch.length) {
      gsap.to(batch, {
        opacity: 1, y: 0,
        duration: 0.9, ease: 'power2.out',
        stagger: 0.08, overwrite: 'auto'
      });
    }
  }

  function initAll(root) {
    root = root || document;
    // Don't prepare anything until the viewport has a real height, or we'd hide
    // content that can never be revealed (0-height/hidden tab). Retry shortly.
    if (vpH() < 1) { setTimeout(function () { initAll(root); }, 200); return; }
    var sections = (root.matches && root.matches('.shopify-section'))
      ? [root]
      : Array.prototype.slice.call(root.querySelectorAll('.shopify-section'));
    sections.forEach(initSection);
    revealPass(); // reveal anything already in view
  }

  function throttle(fn, wait) {
    var last = 0, timer;
    return function () {
      var now = Date.now();
      if (now - last >= wait) { last = now; fn(); }
      else { clearTimeout(timer); timer = setTimeout(function () { last = Date.now(); fn(); }, wait); }
    };
  }

  function boot() {
    if (STARTED) return true;
    if (!window.gsap) return false;
    STARTED = true;

    reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return true; // leave everything visible

    initAll(document);

    var onScroll = throttle(revealPass, 100);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    window.addEventListener('load', revealPass);
    document.addEventListener('shopify:section:load', function (e) {
      if (e && e.target) initAll(e.target);
    });
    // Extra safety for late layout / scroll-restoration.
    setTimeout(revealPass, 500);
    setTimeout(revealPass, 1500);
    return true;
  }

  // GSAP loads with defer — poll briefly until ready.
  function waitForGsap() {
    if (boot()) return;
    var tries = 0;
    var timer = setInterval(function () {
      if (boot() || ++tries > 60) clearInterval(timer);
    }, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForGsap);
  } else {
    waitForGsap();
  }
})();
